import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../config/api';
import useAuthStore from '../../store/authStore';
import { printA4 } from '../../utils/printReceipt';
import InvoiceScan from './InvoiceScan';
import useAddons from '../../hooks/useAddons';

import { DOC_TYPE_MAP, DOC_TYPE_GROUPS } from '../../utils/docTypes';
const DOC_TYPES = Object.fromEntries(Object.entries(DOC_TYPE_MAP).map(([k,v]) => [k, v.label]));
const PURCHASE_TYPES = [100, 120];
const ACCENT = '#2e9f86';

const fmtLocal = (d) => {
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const todayLocal = () => fmtLocal(new Date());

const IconBack    = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
const IconSearch  = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;
const IconPlus    = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>;
const IconX       = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12"/></svg>;
const IconTrash   = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>;
const IconCheck   = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>;
const IconPrint   = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>;
const IconChevron = ({ open }) => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}><polyline points="9 18 15 12 9 6"/></svg>;

const inp = {
  width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 8,
  padding: '7px 11px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  background: '#fafafa', color: '#111',
};
const Field = ({ label, children, span2 }) => (
  <div style={{ gridColumn: span2 ? '1/-1' : undefined }}>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
    {children}
  </div>
);
const Badge = ({ children, color }) => {
  const map = {
    green: { bg: '#d1fae5', text: '#065f46' }, gray: { bg: '#f3f4f6', text: '#6b7280' },
    amber: { bg: '#fef3c7', text: '#92400e' }, red: { bg: '#fee2e2', text: '#991b1b' },
    blue:  { bg: '#dbeafe', text: '#1e40af' },
  };
  const c = map[color] || map.gray;
  return <span style={{ background: c.bg, color: c.text, fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, whiteSpace: 'nowrap', letterSpacing: 0.3 }}>{children}</span>;
};
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const useIsMobile = () => {
  const [m, setM] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const fn = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return m;
};

export default function DocumentForm() {
  const params = useParams();
  const id = params.id || params['*']?.split('/').pop();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const isMobile = useIsMobile();

  const [docType,    setDocType]    = useState(() => {
    const t = Number(searchParams.get('type'));
    return DOC_TYPE_MAP[t] ? t : 200;
  });
  const [accountId,  setAccountId]  = useState('');
  const [date,       setDate]       = useState(todayLocal());
  const [notes,      setNotes]      = useState('');
  const [currency,   setCurrency]   = useState('USD');
  const [useTax,     setUseTax]     = useState(false);
  const [useDiscount,setUseDiscount]= useState(false);
  const [docId,      setDocId]      = useState(null);
  const [docStatus,  setDocStatus]  = useState('draft');
  const [docNumber,  setDocNumber]  = useState(null);
  const [headerSaved,setHeaderSaved]= useState(false);
  const [search,     setSearch]     = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity,   setQuantity]   = useState(1);
  const [price,      setPrice]      = useState('');
  const [tax,        setTax]        = useState(0);
  const [discount,   setDiscount]   = useState(0);
  const [serial,     setSerial]     = useState('');
  const [panelMode,  setPanelMode]  = useState(null);
  const [showScan,   setShowScan]   = useState(false); // 📷 invoice scan (purchases)
  const addons = useAddons();                          // paid AI add-ons (off by default)
  const [showReturn, setShowReturn] = useState(false); // ↩ sales return modal
  const [payAmount,  setPayAmount]  = useState('');
  const [payMethodId,setPayMethodId]= useState('');
  const [payCurrency,setPayCurrency]= useState('USD');
  const [payNotes,   setPayNotes]   = useState('');
  const [chq,        setChq]        = useState({ number: '', bank: '', date: '' });   // cheque payment
  const [batchNo,    setBatchNo]    = useState('');                                    // purchase line batch
  const [expiry,     setExpiry]     = useState('');                                    // purchase line expiry
  const [showLanded, setShowLanded] = useState(false);                                 // landed cost modal

  const isPurchase = PURCHASE_TYPES.includes(Number(docType));

  // amounts on this page follow the DOCUMENT's currency (no more $ on LBP bills)
  const fmtDoc = (n) => currency === 'LBP'
    ? `${Math.round(Number(n) || 0).toLocaleString('en-US')} LBP`
    : money(n);

  // ── queries ───────────────────────────────────────────────────────────────
  const { data: existingDoc } = useQuery({
    queryKey: ['document', id],
    queryFn:  () => api.get(`/api/documents/${id}`).then(r => r.data),
    enabled:  !!id && id !== 'new',
  });
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn:  () => api.get('/api/settings').then(r => r.data),
  });
  const { data: priceLevels = [] } = useQuery({
    queryKey: ['priceLevels'],
    queryFn:  () => api.get('/api/price-levels').then(r => r.data),
  });
  const { data: accounts = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn:  () => api.get('/api/accounts', { params: { all: true } }).then(r =>
      Array.isArray(r.data) ? r.data : (r.data?.data || [])
    ),
  });

  // filter accounts based on document type role
  const requiredRole = DOC_TYPE_MAP[Number(docType)]?.accountRole;
  const filteredAccounts = React.useMemo(() => {
    if (!requiredRole) return [];  // inventory/loss — no account picker
    return accounts.filter(a =>
      !a.roles?.length || a.roles.includes(requiredRole)
    );
  }, [accounts, requiredRole]);

  const accountLabel = PURCHASE_TYPES.includes(Number(docType))
    ? 'المورد / Supplier'
    : requiredRole === 'customer'
      ? 'العميل / Customer'
      : 'الحساب / Account';
  const { data: products = [] } = useQuery({
    queryKey: ['products', search],
    queryFn:  () => api.get('/api/products', { params: { search, status: 'active', all: true } }).then(r =>
      Array.isArray(r.data) ? r.data : (r.data?.data || [])
    ),
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn:  () => api.get('/api/categories').then(r => r.data),
  });
  const { data: variants = [] } = useQuery({
    queryKey: ['variants', selectedProduct?._id],
    queryFn:  () => api.get(`/api/products/${selectedProduct._id}/variants`).then(r => r.data),
    enabled:  selectedProduct?.type === 'variable',
  });
  const { data: items = [], refetch: refetchItems } = useQuery({
    queryKey: ['document-items', docId],
    queryFn:  () => api.get(`/api/documents/${docId}/items`).then(r => r.data),
    enabled:  !!docId,
  });
  const { data: docData, refetch: refetchDoc } = useQuery({
    queryKey: ['document-totals', docId],
    queryFn:  () => api.get(`/api/documents/${docId}`).then(r => r.data),
    enabled:  !!docId,
  });
  const { data: payments = [], refetch: refetchPayments } = useQuery({
    queryKey: ['payments', docId],
    queryFn:  () => api.get(`/api/documents/${docId}/payments`).then(r => r.data),
    enabled:  !!docId && docStatus !== 'draft',
  });
  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['payment-methods'],
    queryFn:  () => api.get('/api/payment-methods').then(r => r.data),
    enabled:  docStatus !== 'draft',
  });

  const showLBP = settings?.currency?.show_lbp ?? true;
  const rate    = settings?.exchange_rate || 89500;
  const company      = settings?.company || {};
  const trackSerials = !!settings?.features?.track_serials;
  const taxEnabled     = !!settings?.features?.tax;
  const defaultTaxRate = Number(settings?.features?.default_tax_rate) || 0;

  // ── load existing doc ─────────────────────────────────────────────────────
  useEffect(() => {
    if (existingDoc) {
      setDocType(existingDoc.type);
      setAccountId(existingDoc.account_id || '');
      setDate(fmtLocal(new Date(existingDoc.date)));
      setNotes(existingDoc.notes || '');
      setCurrency(existingDoc.currency || 'USD');
      setDocId(existingDoc._id);
      setDocStatus(existingDoc.status);
      setDocNumber(existingDoc.number);
      setHeaderSaved(true);
    }
  }, [existingDoc]);

  // ── resolve price (with price level support) ──────────────────────────────
  // For an LBP document, use the explicit LBP price if set, otherwise derive it
  // from the USD price × exchange rate (most shops only maintain USD prices).
  const lbpFrom = (lbp, usd) => Number(lbp) || Math.round((Number(usd) || 0) * rate);
  const resolvePrice = (product, variant) => {
    if (isPurchase) {
      // purchase price = the product's cost, converted into the document's currency
      const costVal = Number(variant ? variant.cost : product.cost) || 0;
      const costCur = (variant ? variant.cost_currency : product.cost_currency) || product.cost_currency || 'USD';
      const costUSD = costCur === 'LBP' ? costVal / rate : costVal;     // normalise to USD
      return currency === 'LBP' ? Math.round(costUSD * rate) : costUSD; // then into the doc currency
    }
    const account = accounts.find(a => a._id === accountId);
    const levelId = account?.price_level_id;
    if (levelId) {
      const levels = variant ? null : product.price_levels;
      if (levels && levels[levelId]) {
        const lv = levels[levelId];
        return currency === 'LBP' ? lbpFrom(lv.lbp, lv.usd) : (lv.usd || lv.lbp);
      }
    }
    if (variant) {
      // fall back to parent product's price if variant has no price set (0 = empty)
      const varPrice  = currency === 'LBP' ? lbpFrom(variant.price_lbp, variant.price_usd) : variant.price_usd;
      const prodPrice = currency === 'LBP' ? lbpFrom(product.price_lbp, product.price_usd) : product.price_usd;
      return varPrice || prodPrice;
    }
    return currency === 'LBP' ? lbpFrom(product.price_lbp, product.price_usd) : product.price_usd;
  };

  useEffect(() => {
    if (!selectedProduct) return;
    if (selectedProduct.type === 'variable') {
      if (selectedVariant) setPrice(resolvePrice(selectedProduct, selectedVariant) || '');
    } else {
      setPrice(resolvePrice(selectedProduct, null) || '');
    }
  }, [selectedProduct, selectedVariant, isPurchase, accountId, currency]);

  // Auto-calculate tax as INCLUSIVE extraction: tax = price × qty × rate / (1 + rate)
  // This keeps the selling price the same as the POS price.
  // e.g. $10 price, 11% VAT → tax = $10 × 0.11/1.11 = $0.99 → total stays $10.
  useEffect(() => {
    if (!useTax || defaultTaxRate <= 0) {
      setTax(0);
      return;
    }
    const base = Number(price) * Number(quantity);
    const amount = base * (defaultTaxRate / 100) / (1 + defaultTaxRate / 100);
    setTax(Number(amount.toFixed(4)));
  }, [useTax, price, quantity, defaultTaxRate]);

  useEffect(() => { if (variants.length > 0) setSelectedVariant(variants[0]); }, [variants]);
  useEffect(() => { if (paymentMethods.length > 0 && !payMethodId) setPayMethodId(paymentMethods[0]._id); }, [paymentMethods]);

  // ── print A4 helper ───────────────────────────────────────────────────────
  const handlePrint = () => {
    printA4({
      company,
      document: { ...docData, number: docNumber, account_id: accountId, date, notes, type: Number(docType) },
      items,
      totals: docData?.totals || {},
      payments,
      accounts,
      showLBP,
      rate,
    });
  };

  // ── mutations ─────────────────────────────────────────────────────────────
  const createDocMutation = useMutation({
    mutationFn: (data) => api.post('/api/documents', data),
    onSuccess: (res) => {
      setDocId(res.data._id); setHeaderSaved(true);
      navigate(`/documents/${res.data._id}`, { replace: true });
    },
    onError: (err) => alert(err.response?.data?.error || 'Error creating document'),
  });
  const updateDocMutation = useMutation({
    mutationFn: (data) => api.put(`/api/documents/${docId}`, data),
    onSuccess: () => setHeaderSaved(true),
    onError: (err) => alert(err.response?.data?.error || 'Error updating document'),
  });
  const addItemMutation = useMutation({
    mutationFn: (data) => api.post(`/api/documents/${docId}/items`, data),
    onSuccess: () => { refetchItems(); refetchDoc(); resetItemForm(); setPanelMode(null); },
    onError: (err) => alert(err.response?.data?.error || 'Error adding item'),
  });
  const deleteItemMutation = useMutation({
    mutationFn: (itemId) => api.delete(`/api/documents/${docId}/items/${itemId}`),
    onSuccess: () => { refetchItems(); refetchDoc(); },
  });
  const confirmMutation = useMutation({
    mutationFn: (body) => api.post(`/api/documents/${docId}/confirm`, body || {}),
onSuccess: async (res) => {
  setDocStatus('confirmed');
  setDocNumber(res.data.number);
  await refetchDoc();
  await refetchItems();
  queryClient.invalidateQueries(['documents']);
},
    onError: (err) => {
      const d = err.response?.data;
      // over the customer's credit limit: the owner / a manager may confirm anyway
      if (d?.reason === 'credit_limit' && ['admin', 'manager'].includes(user?.role)) {
        if (window.confirm(`${d.error}\n\nConfirm anyway? (recorded in the audit trail)`)) confirmMutation.mutate({ override_credit_limit: true });
        return;
      }
      alert(d?.error || 'Error confirming document');
    },
  });
  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/api/documents/${docId}/cancel`),
    onSuccess: () => { setDocStatus('cancelled'); queryClient.invalidateQueries(['documents']); },
  });
  const convertMutation = useMutation({
    mutationFn: () => api.post(`/api/documents/${docId}/convert`).then(r => r.data),
    onSuccess: (newDoc) => {
      queryClient.invalidateQueries(['documents']);
      navigate(`/documents/${newDoc._id}`);
    },
    onError: (err) => alert(err.response?.data?.error || 'Error converting document'),
  });

  const addPaymentMutation = useMutation({
    mutationFn: (data) => api.post(`/api/documents/${docId}/payments`, data),
    onSuccess: () => { refetchPayments(); refetchDoc(); setPayAmount(''); setPayNotes(''); setChq({ number: '', bank: '', date: '' }); },
    onError: (err) => {
      const data = err.response?.data;
      if (data?.reason === 'overpayment') {
        alert(`Payment exceeds the remaining balance (${fmtDoc(data.remaining_balance)}).`);
      } else {
        alert(data?.error || 'Error adding payment');
      }
    },
  });
  const voidPaymentMutation = useMutation({
    mutationFn: ({ paymentId, reason }) => api.delete(`/api/documents/${docId}/payments/${paymentId}`, { data: { reason } }),
    onSuccess: () => { refetchPayments(); refetchDoc(); },
    onError: (err) => alert(err.response?.data?.error || 'Error voiding payment'),
  });
  const handleVoidPayment = (paymentId) => {
    const reason = window.prompt('Reason for voiding this payment (optional):', '');
    if (reason === null) return; // cancelled
    voidPaymentMutation.mutate({ paymentId, reason });
  };

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleSelectProduct = (product) => {
    setSelectedProduct(product); setSelectedVariant(null);
    setQuantity(1); setDiscount(0); setSerial(''); setPanelMode('item');
    // tax will be recalculated by the auto-tax useEffect
  };
  const resetItemForm = () => {
    setSelectedProduct(null); setSelectedVariant(null);
    setQuantity(1); setPrice(''); setTax(0); setDiscount(0); setSerial('');
  };
  const closePanel = () => { setPanelMode(null); resetItemForm(); };

  const handleSaveHeader = () => {
    const data = { type: Number(docType), account_id: accountId || null, date, notes, currency };
    if (id === 'new' && !docId) createDocMutation.mutate(data);
    else if (docId) updateDocMutation.mutate(data);
  };
  const handleAddItem = () => {
    if (!selectedProduct || !quantity || price === '') return;
    addItemMutation.mutate({
      product_id:    selectedProduct._id,
      variant_id:    selectedVariant?._id || null,
      product_name:  selectedProduct.name,
      variant_name:  selectedVariant?.name || '',
      quantity:      Number(quantity),
      price:         Number(price),
      price_currency: currency,
      cost:          isPurchase ? Number(price) : (selectedVariant?.cost || selectedProduct.cost || 0),
      cost_currency: isPurchase ? currency : 'USD',
      tax:           useTax ? Number(tax) : 0,
      discount:      useDiscount ? Number(discount) : 0,
      serial:        serial || '',
      ...(Number(docType) === 100 && batchNo ? { batch_no: batchNo } : {}),
      ...(Number(docType) === 100 && expiry ? { expiry_date: expiry } : {}),
    });
    setBatchNo(''); setExpiry('');
  };
  const payIsCheque = (paymentMethods.find(m => String(m._id) === String(payMethodId)) || {}).type === 'cheque';
  const handleAddPayment = () => {
    if (!payAmount || !payMethodId) return;
    addPaymentMutation.mutate({
      payment_method_id: payMethodId,
      amount:   Number(payAmount),
      currency: payCurrency,
      notes:    payNotes,
      ...(payIsCheque ? { cheque_number: chq.number, cheque_bank: chq.bank, cheque_date: chq.date || undefined } : {}),
    });
  };

  // Flat product picker list — no category grouping, capped at 50 rendered rows
  // (search already narrows the underlying query; this just bounds render size).
  const PICKER_LIMIT = 50;
  const pickerProducts = products.slice(0, PICKER_LIMIT);
  const pickerTruncated = products.length > PICKER_LIMIT;

  const totals    = docData?.totals || { subtotal: 0, tax: 0, discount: 0, total: 0, paid: 0, balance: 0 };
  const isDraft      = docStatus === 'draft';
  const isConfirmed  = docStatus === 'confirmed';
  const isProforma   = Number(docType) === 230;
  const isInventory  = [300, 400].includes(Number(docType)); // no payments for internal docs
  const docTypeInfo  = DOC_TYPE_MAP[Number(docType)];
  // resolved account object (for WhatsApp button)
  const docAccount   = accounts.find(a => String(a._id) === String(accountId || docData?.account_id));

  // Build a WhatsApp message with the document details
  const buildWhatsAppMsg = () => {
    const shopName = settings?.company?.name || 'MMM POS';
    const typeLabel = docTypeInfo ? `${docTypeInfo.icon} ${docTypeInfo.labelAr}` : 'مستند';
    const dateStr = docData?.date ? new Date(docData.date).toLocaleDateString('ar-LB') : '';
    const cur = docData?.currency === 'LBP' ? 'ل.ل' : '$';
    const fmt = (n) => docData?.currency === 'LBP'
      ? Math.round(n || 0).toLocaleString('en-US')
      : (n || 0).toFixed(2);

    const lines = items.map(it => {
      const name = it.product_name || it.name || '';
      const variant = it.variant_name ? ` (${it.variant_name})` : '';
      const qty = Number(it.quantity || 1);
      const price = Number(it.unit_price || it.price || 0);
      const total = Number(it.total || qty * price);
      return `• ${name}${variant}  x${qty}  ${cur}${fmt(total)}`;
    }).join('\n');

    const totalsBlock = [
      totals.discount > 0 ? `خصم: ${cur}${fmt(totals.discount)}` : '',
      totals.tax      > 0 ? `ضريبة: ${cur}${fmt(totals.tax)}` : '',
      `*الإجمالي: ${cur}${fmt(totals.total)}*`,
      isConfirmed && totals.paid > 0 ? `مدفوع: ${cur}${fmt(totals.paid)}` : '',
      isConfirmed && totals.balance > 0 ? `*المتبقي: ${cur}${fmt(totals.balance)}*` : '',
    ].filter(Boolean).join('\n');

    return [
      `*${shopName}*`,
      `${typeLabel}${docNumber ? ` — رقم ${docNumber}` : ''}`,
      dateStr ? `التاريخ: ${dateStr}` : '',
      docAccount?.name ? `${PURCHASE_TYPES.includes(Number(docType)) ? 'المورد' : 'العميل'}: ${docAccount.name}` : '',
      '',
      lines,
      '',
      totalsBlock,
      notes ? `\nملاحظة: ${notes}` : '',
    ].filter(s => s !== undefined).join('\n').trim();
  };

  const openWhatsApp = () => {
    if (!docAccount?.phone) return;
    const phone = docAccount.phone.replace(/\D/g, '');
    const msg = encodeURIComponent(buildWhatsAppMsg());
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };
  const itemCols  = 3 + (useTax ? 1 : 0) + (useDiscount ? 1 : 0) + (isDraft ? 1 : 0);
  const statusColor = isConfirmed ? 'green' : docStatus === 'cancelled' ? 'red' : 'amber';

  const paidBadge = () => {
    if (!isConfirmed) return null;
    const total = totals.total || 0, paid = totals.paid || 0;
    if (paid <= 0)     return <Badge color="red">Unpaid</Badge>;
    if (paid >= total) return <Badge color="green">Paid</Badge>;
    return <Badge color="amber">Partial</Badge>;
  };

  // Tax is inclusive (extracted from price) — don't add it on top of the line total
  const lineTotal = ((Number(quantity) * Number(price)) - Number(useDiscount ? discount : 0)) || 0;

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', fontFamily: "'DM Sans','Segoe UI',sans-serif", background: '#f8f9fa' }}>

      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1.5px solid #f0f0f0', padding: '12px 18px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/documents')} style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
            <IconBack /> Back
          </button>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#111', margin: 0 }}>
            {docNumber || (id === 'new' ? 'New Document' : 'Document')}
          </h2>
          <Badge color={statusColor}>{docStatus}</Badge>
          {paidBadge()}
          {docTypeInfo && (
            <span style={{ fontSize: 12, background: docTypeInfo.bg, color: docTypeInfo.color, borderRadius: 20, padding: '2px 9px', fontWeight: 600 }}>
              {docTypeInfo.icon} {docTypeInfo.labelAr}
            </span>
          )}
          {docData?.converted_from && (
            <span style={{ fontSize: 11, background: '#f5f3ff', color: '#6d28d9', borderRadius: 20, padding: '2px 9px', fontWeight: 600 }}>
              📋 محوّل من عرض سعر
            </span>
          )}
          {docAccount?.name && (
            <span style={{ fontSize: 13, color: '#374151', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              {docAccount.name}
              {docAccount.phone && items.length > 0 && (
                <button onClick={openWhatsApp}
                  title={`إرسال عبر WhatsApp إلى ${docAccount.phone}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#dcfce7', color: '#16a34a', border: 'none', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  <span style={{ fontSize: 14 }}>💬</span> WhatsApp
                </button>
              )}
            </span>
          )}

          <div style={{ display: 'flex', gap: 8, marginInlineStart: 'auto' }}>
            {/* Print button — visible on confirmed documents */}
            {isConfirmed && (
              <button onClick={handlePrint} style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <IconPrint /> Print
              </button>
            )}

            {/* Return button — confirmed Sales Out only */}
            {isConfirmed && Number(docType) === 200 && (
              <button onClick={() => setShowReturn(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#fef2f2', color: '#dc2626', border: '1.5px solid #fca5a5', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                ↩ Return
              </button>
            )}

            {/* Convert Proforma → Sales Invoice */}
            {isConfirmed && isProforma && (
              <button
                onClick={() => {
                  if (window.confirm('تحويل عرض السعر لفاتورة بيع؟ ستُنشأ مسودة جديدة يمكنك تعديلها.\nConvert quote to Sales Invoice? A new editable draft will be created.'))
                    convertMutation.mutate();
                }}
                disabled={convertMutation.isPending}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2e9f86', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: convertMutation.isPending ? 0.6 : 1 }}>
                {convertMutation.isPending ? '…' : '🧾 تحويل لفاتورة / Convert'}
              </button>
            )}

            {isDraft && headerSaved && isPurchase && addons.ai_scan && (
              <button onClick={() => setShowScan(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#eff6ff', color: '#1d4ed8', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                📷 Scan Invoice
              </button>
            )}
            {isDraft && headerSaved && (
              <>
                <button
                  onClick={() => {
                    const msg = isProforma
                      ? 'تأكيد عرض السعر؟ لن يتأثر المخزون.\nConfirm quote? No stock change.'
                      : `تأكيد المستند؟ سيتم تحديث المخزون.\nConfirm? Stock will be updated.`;
                    if (window.confirm(msg)) confirmMutation.mutate();
                  }}
                  disabled={confirmMutation.isPending || items.length === 0}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (confirmMutation.isPending || items.length === 0) ? 0.5 : 1 }}>
                  <IconCheck /> {confirmMutation.isPending ? 'Confirming…' : 'Confirm'}
                </button>
                <button onClick={() => { if (window.confirm('إلغاء هذا المستند؟ Cancel this document?')) cancelMutation.mutate(); }}
                  style={{ background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel Doc
                </button>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, alignItems: 'end' }}>
          <Field label="النوع / Type">
            <select value={docType} onChange={e => { setDocType(e.target.value); setAccountId(''); }} disabled={!isDraft || headerSaved} style={{ ...inp, background: (!isDraft || headerSaved) ? '#f3f4f6' : '#fafafa' }}>
              {DOC_TYPE_GROUPS.map(g => (
                <optgroup key={g.group} label={g.group}>
                  {g.types.map(code => (
                    <option key={code} value={code}>{DOC_TYPE_MAP[code].icon} {DOC_TYPE_MAP[code].labelAr} / {DOC_TYPE_MAP[code].labelEn}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
          {requiredRole && (
            <Field label={accountLabel}>
              <select value={accountId} onChange={e => setAccountId(e.target.value)} disabled={!isDraft} style={{ ...inp, background: !isDraft ? '#f3f4f6' : '#fafafa' }}>
                <option value="">— اختر / Select —</option>
                {filteredAccounts.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Date">
            <input type="date" value={date} onChange={e => setDate(e.target.value)} disabled={!isDraft} style={{ ...inp, background: !isDraft ? '#f3f4f6' : '#fafafa' }} />
          </Field>
          <Field label="Currency">
            <select value={currency} onChange={e => setCurrency(e.target.value)} disabled={!isDraft} style={{ ...inp, background: !isDraft ? '#f3f4f6' : '#fafafa' }}>
              <option value="USD">USD</option>
              {showLBP && <option value="LBP">LBP</option>}
            </select>
          </Field>
          <Field label="Notes">
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)} disabled={!isDraft} style={{ ...inp, background: !isDraft ? '#f3f4f6' : '#fafafa' }} />
          </Field>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', paddingBottom: 6 }}>
            {taxEnabled && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: useTax ? ACCENT : '#6b7280', cursor: isDraft ? 'pointer' : 'default' }}>
                <input type="checkbox" checked={useTax} onChange={e => setUseTax(e.target.checked)} disabled={!isDraft} style={{ width: 15, height: 15, accentColor: ACCENT }} />
                VAT {defaultTaxRate > 0 ? `${defaultTaxRate}%` : ''}
              </label>
            )}
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: '#6b7280', cursor: 'pointer' }}>
              <input type="checkbox" checked={useDiscount} onChange={e => setUseDiscount(e.target.checked)} disabled={!isDraft} style={{ width: 15, height: 15, accentColor: ACCENT }} /> Disc.
            </label>
          </div>
          {isDraft && (
            <button onClick={handleSaveHeader} disabled={createDocMutation.isPending || updateDocMutation.isPending}
              style={{ background: headerSaved ? '#f3f4f6' : ACCENT, color: headerSaved ? '#374151' : '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', height: 36 }}>
              {headerSaved ? 'تعديل / Update' : 'حفظ وابدأ / Save & Start'}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {!headerSaved ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 14 }}>
          أكمل البيانات أعلاه ثم اضغط "حفظ وابدأ" لإضافة المنتجات.
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', minHeight: 0, gap: 12, padding: 12, overflowY: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch' }}>

          {/* Left: product picker (full-width, capped height on mobile) */}
          {isDraft && (
            <div style={{ width: isMobile ? '100%' : 280, maxHeight: isMobile ? 300 : undefined, boxSizing: 'border-box', background: '#fff', borderRadius: 12, border: '1.5px solid #f0f0f0', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <div style={{ padding: 10, borderBottom: '1.5px solid #f0f0f0', position: 'relative' }}>
                <span style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}><IconSearch /></span>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search product…" style={{ ...inp, paddingLeft: 32 }} />
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
                {pickerProducts.length === 0 ? (
                  <div style={{ padding: '24px 10px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No products found.</div>
                ) : pickerProducts.map(p => (
                  <button key={p._id} onClick={() => handleSelectProduct(p)}
                    style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 10px', fontSize: 13, borderRadius: 8, border: 'none', cursor: 'pointer',
                      background: selectedProduct?._id === p._id ? '#d1fae5' : 'transparent',
                      color: selectedProduct?._id === p._id ? '#065f46' : '#374151',
                      fontWeight: selectedProduct?._id === p._id ? 600 : 400 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 6 }}>{p.name}</span>
                    <span style={{ color: '#9ca3af', fontSize: 12, flexShrink: 0 }}>{p.type === 'variable' ? '▸' : p.price_usd ? `$${p.price_usd}` : ''}</span>
                  </button>
                ))}
                {pickerTruncated && (
                  <div style={{ padding: '8px 10px', fontSize: 11, color: '#9ca3af', textAlign: 'center' }}>
                    Showing {PICKER_LIMIT} of {products.length} — search to narrow down
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Center: items table */}
          <div style={{ flex: isMobile ? 'none' : 1, minWidth: 0, background: '#fff', borderRadius: 12, border: '1.5px solid #f0f0f0', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <div style={{ flex: isMobile ? 'none' : 1, overflowY: isMobile ? 'visible' : 'auto', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#fff', borderBottom: '1.5px solid #f0f0f0' }}>
                    {['Product', 'Qty', 'Price', ...(useTax ? ['Tax'] : []), ...(useDiscount ? ['Disc'] : []), 'Total', ...(isDraft ? [''] : [])].map((h, i) => (
                      <th key={i} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={itemCols} style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
                      {isDraft ? 'No items yet — pick a product from the left.' : 'No items.'}
                    </td></tr>
                  ) : items.map((item, idx) => (
                    <tr key={item._id} style={{ background: idx % 2 ? '#fafafa' : '#fff', borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '9px 14px' }}>
                        <div style={{ fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{item.product_name}</div>
                        {item.variant_name && <div style={{ fontSize: 11, color: ACCENT, fontWeight: 600 }}>{item.variant_name}</div>}
                        {item.serial && <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace', marginTop: 1 }}>S/N: {item.serial}</div>}
                      </td>
                      <td style={{ padding: '9px 14px', color: '#374151', fontWeight: 600 }}>{item.quantity}</td>
                      <td style={{ padding: '9px 14px', color: '#374151' }}>{fmtDoc(item.price)}</td>
                      {useTax      && <td style={{ padding: '9px 14px', color: '#6b7280' }}>{item.tax || 0}</td>}
                      {useDiscount && <td style={{ padding: '9px 14px', color: '#6b7280' }}>{item.discount || 0}</td>}
                      <td style={{ padding: '9px 14px', fontWeight: 700, color: '#111' }}>{fmtDoc(item.total)}</td>
                      {isDraft && (
                        <td style={{ padding: '9px 14px', textAlign: 'center' }}>
                          <button onClick={() => deleteItemMutation.mutate(item._id)} style={{ background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '5px 7px', cursor: 'pointer', lineHeight: 1 }}><IconTrash /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
<div style={{ borderTop: '1.5px solid #f0f0f0', padding: '10px 16px', background: '#f8f9fa', borderRadius: docData?.delivery?.enabled ? '0' : '0 0 12px 12px', display: 'flex', justifyContent: 'flex-end', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
  <span style={{ fontSize: 13, color: '#6b7280' }}>Subtotal: <strong style={{ color: '#111' }}>{fmtDoc(totals.subtotal)}</strong></span>
  {useTax      && <span style={{ fontSize: 13, color: '#6b7280' }}>Tax: <strong style={{ color: '#111' }}>{fmtDoc(totals.tax)}</strong></span>}
  {useDiscount && <span style={{ fontSize: 13, color: '#6b7280' }}>Disc: <strong style={{ color: '#111' }}>{fmtDoc(totals.discount)}</strong></span>}
  <span style={{ fontSize: 16, fontWeight: 700, color: ACCENT }}>Total: {fmtDoc(totals.total)}</span>
</div>

{docData?.delivery?.enabled && (
  <div style={{ borderTop: '1.5px solid #f0f0f0', padding: '12px 16px', background: '#fffbeb', borderRadius: '0 0 12px 12px', display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
    <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5 }}>🚚 Delivery</span>
    {docData.delivery.address && (
      <span style={{ fontSize: 13, color: '#78350f' }}>📍 {docData.delivery.address}</span>
    )}
    {docData.delivery.phone && (
      <span style={{ fontSize: 13, color: '#78350f' }}>📞 {docData.delivery.phone}</span>
    )}
    {docData.delivery.fee > 0 && (
      <span style={{ fontSize: 13, color: '#78350f' }}>Fee: <strong>{money(docData.delivery.fee)}</strong></span>
    )}
    {docData.delivery.notes && (
      <span style={{ fontSize: 13, color: '#78350f' }}>Note: {docData.delivery.notes}</span>
    )}
    <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 20,
      background: docData.delivery.status === 'delivered' ? '#d1fae5' : docData.delivery.status === 'dispatched' ? '#dbeafe' : docData.delivery.status === 'failed' ? '#fee2e2' : '#fef3c7',
      color:      docData.delivery.status === 'delivered' ? '#065f46' : docData.delivery.status === 'dispatched' ? '#1e40af' : docData.delivery.status === 'failed' ? '#991b1b' : '#92400e',
    }}>{docData.delivery.status || 'pending'}</span>
  </div>
)}
          </div>

          {/* Right: payments — hidden for Proforma (230), Stock Count (300), Loss (400) */}
          {isConfirmed && !isProforma && !isInventory && (
            <div style={{ width: isMobile ? '100%' : 360, boxSizing: 'border-box', background: '#fff', borderRadius: 12, border: '1.5px solid #f0f0f0', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1.5px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>Payments</span>
                {(totals.balance || 0) > 0 && (
                  <button onClick={() => setPanelMode('payment')} style={{ display: 'flex', alignItems: 'center', gap: 5, background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    <IconPlus /> Add
                  </button>
                )}
              </div>
              <div style={{ padding: 12, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                {[
                  { label: 'Total',   val: totals.total,   c: '#111',    bg: '#f8f9fa' },
                  { label: 'Paid',    val: totals.paid,    c: '#065f46', bg: '#ecfdf5' },
                  { label: 'Balance', val: totals.balance, c: (totals.balance || 0) <= 0 ? '#065f46' : '#dc2626', bg: (totals.balance || 0) <= 0 ? '#ecfdf5' : '#fef2f2' },
                ].map(card => (
                  <div key={card.label} style={{ background: card.bg, borderRadius: 8, padding: '8px 6px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>{card.label}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: card.c }}>{fmtDoc(card.val)}</div>
                  </div>
                ))}
              </div>
              <DocExtras doc={docData} docId={docId} canEdit={['admin', 'manager'].includes(user?.role)} onChanged={refetchDoc} onLanded={() => setShowLanded(true)} />
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
                {payments.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: 24, fontSize: 13 }}>No payments yet.</div>
                ) : payments.map(p => (
                  <div key={p._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 11px', border: '1.5px solid #e5e7eb', borderRadius: 8, marginBottom: 8, opacity: p.status === 'voided' ? 0.55 : 1 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13, color: p.amount < 0 ? '#c2410c' : '#111' }}>
                        {p.amount < 0 ? '−' : ''}{money(Math.abs(p.amount))} <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>{p.currency}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>{p.payment_method_name} · {new Date(p.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                        {p.is_refund && <span style={{ background: '#ffedd5', color: '#c2410c', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600 }}>Refund</span>}
                        {p.status === 'voided' && <span style={{ background: '#e5e7eb', color: '#6b7280', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600 }}>Voided</span>}
                        {p.method_type === 'cheque' && <span style={{ background: p.cheque_status === 'bounced' ? '#fee2e2' : p.cheque_status === 'cleared' ? '#d1fae5' : '#fef3c7', color: '#374151', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600 }}>Cheque {p.cheque_number || ''} · {p.cheque_status === 'cleared' ? 'cashed' : p.cheque_status === 'bounced' ? 'BOUNCED' : 'waiting'}</span>}
                        {(p.status === 'refunded' || p.refunded) && <span style={{ background: '#ffedd5', color: '#c2410c', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600 }}>Refunded</span>}
                        {p.edit_history?.length > 0 && <span style={{ background: '#dbeafe', color: '#1d4ed8', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600 }}>Edited ×{p.edit_history.length}</span>}
                      </div>
                    </div>
                    {isAdmin && p.status === 'completed' && !p.refunded && (
                      <button onClick={() => handleVoidPayment(p._id)} title="Void payment" style={{ background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '5px 7px', cursor: 'pointer', lineHeight: 1 }}><IconTrash /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showLanded && docId && <LandedCostModal docId={docId} paymentMethods={paymentMethods} onClose={() => setShowLanded(false)} />}
      {/* ↩ sales return modal */}
      {showReturn && docId && (
        <ReturnModal
          docId={docId}
          docNumber={docData?.number}
          accountId={accountId}
          items={items}
          paymentMethods={paymentMethods}
          onClose={() => setShowReturn(false)}
          onCreated={(newId) => { setShowReturn(false); navigate(`/documents/${newId}`); }}
        />
      )}

      {/* 📷 invoice scan modal */}
      {showScan && docId && (
        <InvoiceScan
          docId={docId}
          products={products}
          accounts={accounts}
          accountId={accountId}
          rate={rate}
          docCurrency={currency}
          categories={categories}
          priceLevels={priceLevels}
          plEnabled={!!settings?.features?.price_levels}
          onAccountMatched={(id) => setAccountId(id)}
          onImported={() => { refetchItems(); refetchDoc(); }}
          onClose={() => setShowScan(false)}
        />
      )}

      {/* Slide-in panels */}
      <>
        {panelMode && <div onClick={closePanel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 40, backdropFilter: 'blur(2px)' }} />}
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: panelMode ? (isMobile ? '100%' : 420) : 0, maxWidth: '100vw', overflow: 'hidden', background: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 50, transition: 'width .3s cubic-bezier(.4,0,.2,1)', display: 'flex', flexDirection: 'column' }}>

          {panelMode === 'item' && selectedProduct && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1.5px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>Add Item</div>
                  <div style={{ fontSize: 12, color: ACCENT, fontWeight: 600, marginTop: 2 }}>{selectedProduct.name}</div>
                </div>
                <button onClick={closePanel} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}><IconX /></button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {selectedProduct.type === 'variable' && (
                  <Field label="Variant *">
                    <select value={selectedVariant?._id || ''} onChange={e => setSelectedVariant(variants.find(v => v._id === e.target.value))} style={inp}>
                      <option value="">Select Variant</option>
                      {variants.map(v => <option key={v._id} value={v._id}>{v.name} {v.attributes?.map(a => a.value).join('/')}</option>)}
                    </select>
                  </Field>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Quantity">
                    <input type="number" value={quantity} min="1" onChange={e => setQuantity(e.target.value)} style={inp} />
                  </Field>
                  <Field label={`Price (${currency})`}>
                    <input type="number" value={price} min="0" step="0.01" onChange={e => setPrice(e.target.value)} style={{ ...inp, borderColor: ACCENT, background: '#f0fdf8' }} />
                  </Field>
                  {useDiscount && (
                    <Field label="Discount Amount">
                      <input type="number" value={discount} min="0" onChange={e => setDiscount(e.target.value)} style={inp} />
                    </Field>
                  )}
                </div>
                <div style={{ background: '#f8f9fa', borderRadius: 10, padding: 14, textAlign: 'center', border: '1.5px solid #e5e7eb' }}>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>Line Total</span>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#111' }}>{fmtDoc(lineTotal)}</div>
                </div>
                {/* batch / expiry — purchase invoices only (Stock → Expiry) */}
                {Number(docType) === 100 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Batch / lot (optional)"><input type="text" value={batchNo} onChange={e => setBatchNo(e.target.value)} style={inp} /></Field>
                    <Field label="Expiry date (optional)"><input type="date" value={expiry} onChange={e => setExpiry(e.target.value)} style={inp} /></Field>
                  </div>
                )}
                {/* Serial / IMEI field — shown only when track_serials is on */}
                {trackSerials && (
                  <Field label="Serial / IMEI (optional)">
                    <input
                      type="text"
                      value={serial}
                      onChange={e => setSerial(e.target.value)}
                      placeholder="Scan or type serial number..."
                      style={{ ...inp, fontFamily: 'monospace' }}
                    />
                  </Field>
                )}
              </div>
              <div style={{ padding: 20, borderTop: '1.5px solid #f0f0f0', display: 'flex', gap: 10 }}>
                <button onClick={handleAddItem}
                  disabled={!selectedProduct || !quantity || price === '' || addItemMutation.isPending || (selectedProduct.type === 'variable' && !selectedVariant)}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '11px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (!quantity || price === '' || (selectedProduct.type === 'variable' && !selectedVariant)) ? 0.5 : 1 }}>
                  <IconPlus /> {addItemMutation.isPending ? 'Adding…' : 'Add Item'}
                </button>
                <button onClick={closePanel} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Close</button>
              </div>
            </>
          )}

          {panelMode === 'payment' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1.5px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#111' }}>Record Payment</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Balance due: <strong style={{ color: '#dc2626' }}>{fmtDoc(totals.balance)}</strong></div>
                </div>
                <button onClick={() => setPanelMode(null)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}><IconX /></button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, alignContent: 'start' }}>
                <Field label="Method">
                  <select value={payMethodId} onChange={e => setPayMethodId(e.target.value)} style={inp}>
                    {paymentMethods.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
                  </select>
                </Field>
                <Field label="Currency">
                  <select value={payCurrency} onChange={e => setPayCurrency(e.target.value)} style={inp}>
                    <option value="USD">USD</option>
                    {showLBP && <option value="LBP">LBP</option>}
                  </select>
                </Field>
                <Field label="Amount" span2>
                  <input type="number" value={payAmount} min="0" step="0.01" placeholder={`Max ${fmtDoc(totals.balance)}`} onChange={e => setPayAmount(e.target.value)} style={{ ...inp, borderColor: ACCENT, background: '#f0fdf8' }} />
                </Field>
                {payIsCheque && <>
                  <Field label="Cheque number"><input type="text" value={chq.number} onChange={e => setChq({ ...chq, number: e.target.value })} style={inp} /></Field>
                  <Field label="Bank"><input type="text" value={chq.bank} onChange={e => setChq({ ...chq, bank: e.target.value })} style={inp} /></Field>
                  <Field label="Cheque date (can be cashed on)" span2><input type="date" value={chq.date} onChange={e => setChq({ ...chq, date: e.target.value })} style={inp} /></Field>
                </>}
                <Field label="Notes (optional)" span2>
                  <input type="text" value={payNotes} placeholder="e.g. Partial payment" onChange={e => setPayNotes(e.target.value)} style={inp} />
                </Field>
              </div>
              <div style={{ padding: 20, borderTop: '1.5px solid #f0f0f0', display: 'flex', gap: 10 }}>
                <button onClick={handleAddPayment} disabled={!payAmount || !payMethodId || addPaymentMutation.isPending}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '11px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (!payAmount || !payMethodId) ? 0.5 : 1 }}>
                  <IconPlus /> {addPaymentMutation.isPending ? 'Saving…' : 'Record Payment'}
                </button>
                <button onClick={() => setPanelMode(null)} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Close</button>
              </div>
            </>
          )}
        </div>
      </>
    </div>
  );
}

// ── ReturnModal ───────────────────────────────────────────────────────────────
function ReturnModal({ docId, docNumber, accountId, items, paymentMethods, onClose, onCreated }) {
  const todayLocal = () => { const d = new Date(); const p = x => String(x).padStart(2,'0'); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}`; };
  const [qtys,   setQtys]   = useState(() => Object.fromEntries(items.map(i => [i._id, i.quantity])));
  const [checks, setChecks] = useState(() => Object.fromEntries(items.map(i => [i._id, true])));
  const [busy,   setBusy]   = useState(false);
  const [err,    setErr]    = useState('');

  const selected = items.filter(i => checks[i._id] && Number(qtys[i._id]) > 0);
  const totalReturn = selected.reduce((s, i) => s + i.price * Number(qtys[i._id]), 0);

  const handleConfirm = async () => {
    if (!selected.length) { setErr('Select at least one item to return.'); return; }
    setBusy(true); setErr('');
    try {
      // 1. Create SR draft
      const doc = await api.post('/api/documents', {
        type: 220, account_id: accountId || null,
        date: todayLocal(), currency: 'USD',
        notes: `مرتجع من ${docNumber || docId} / Return from ${docNumber || docId}`,
      }).then(r => r.data?.data ?? r.data);
      const newId = doc?._id ?? doc?.id;
      if (!newId) throw new Error('Failed to create return document');

      // 2. Add items
      await api.post(`/api/documents/${newId}/items/bulk`, {
        items: selected.map(i => ({
          product_id:    String(i.product_id),
          variant_id:    i.variant_id ? String(i.variant_id) : undefined,
          product_name:  i.product_name,
          variant_name:  i.variant_name || '',
          quantity:      Number(qtys[i._id]),
          price:         i.price,
          price_currency:'USD',
          cost:          i.cost || 0,
          cost_currency: 'USD',
          tax: 0, discount: 0,
        })),
      });

      // 3. Confirm
      await api.post(`/api/documents/${newId}/confirm`, {});
      onCreated(newId);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Error');
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 540, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1.5px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>↩ مرتجع بيع / Sales Return</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>from {docNumber || docId} — select items &amp; qty to return</div>
          </div>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>

        {/* Items */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {items.map(i => (
            <div key={i._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
              <input type="checkbox" checked={!!checks[i._id]} onChange={e => setChecks(c => ({ ...c, [i._id]: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: '#2e9f86', cursor: 'pointer', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.product_name}{i.variant_name ? ` — ${i.variant_name}` : ''}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>Unit price: ${Number(i.price).toFixed(2)} · Sold: {i.quantity}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <label style={{ fontSize: 11, color: '#6b7280' }}>Qty:</label>
                <input type="number" min={1} max={i.quantity} step={1}
                  value={qtys[i._id] ?? i.quantity}
                  disabled={!checks[i._id]}
                  onChange={e => setQtys(q => ({ ...q, [i._id]: Math.min(Number(e.target.value) || 1, i.quantity) }))}
                  style={{ width: 60, border: '1.5px solid #e5e7eb', borderRadius: 6, padding: '4px 8px', fontSize: 13, textAlign: 'center', opacity: checks[i._id] ? 1 : 0.4 }} />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1.5px solid #f0f0f0' }}>
          {err && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{err}</div>}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{selected.length} item(s) selected</span>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#dc2626' }}>Return: ${totalReturn.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleConfirm} disabled={busy || !selected.length}
              style={{ flex: 1, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: (busy || !selected.length) ? 0.5 : 1 }}>
              {busy ? 'Creating…' : '↩ Confirm Return'}
            </button>
            <button onClick={onClose} style={{ background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── due date (credit invoices) + landed costs (purchases) ──────────────────────
function DocExtras({ doc, docId, canEdit, onChanged, onLanded }) {
  if (!doc || ![100, 200].includes(Number(doc.type))) return null;
  const due = doc.due_date ? new Date(doc.due_date) : null;
  const late = due && (doc.totals?.balance || 0) > 0.009 ? Math.floor((Date.now() - due.getTime()) / 86400000) : 0;
  const change = async () => {
    const v = window.prompt('Due date (YYYY-MM-DD)', due ? due.toISOString().slice(0, 10) : '');
    if (v === null) return;
    try { await api.patch(`/api/documents/${docId}/due-date`, { due_date: v || null }); onChanged(); } catch (e) { alert(e.response?.data?.error || e.message); }
  };
  return (
    <div style={{ padding: '0 12px 10px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
      <span style={{ color: late > 0 ? '#dc2626' : '#6b7280' }}>
        📅 Due {due ? due.toLocaleDateString('en-GB') : '—'}{late > 0 ? ` · ${late} day(s) late` : ''}
      </span>
      {canEdit && <button onClick={change} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12 }}>change</button>}
      {canEdit && Number(doc.type) === 100 && <button onClick={onLanded} style={{ marginLeft: 'auto', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 12 }}>+ Landed cost</button>}
    </div>
  );
}

function LandedCostModal({ docId, paymentMethods, onClose }) {
  const [f, setF] = useState({ name: 'Freight', amount: '', currency: 'USD', payment_method_id: '', party: '' });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/finance/landed-costs', { ...f, document_id: docId, payment_method_id: f.payment_method_id || null });
      alert('Added. New item costs:\n' + (r.data.allocation || []).map(a => `${a.name}: +$${a.per_unit_now} per unit`).join('\n'));
      onClose();
    } catch (e) { alert(e.response?.data?.error || e.message); } finally { setBusy(false); }
  };
  const box = { width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 14, boxSizing: 'border-box' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 440, padding: 20 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Landed cost</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Freight, customs, transport… paid for this purchase. It is spread over its items by value and added to their cost, so every sale carries the real cost.</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={{ fontSize: 12 }}>What<input style={box} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></label>
          <label style={{ fontSize: 12 }}>Paid to<input style={box} value={f.party} onChange={e => setF({ ...f, party: e.target.value })} /></label>
          <label style={{ fontSize: 12 }}>Amount<input type="number" style={box} value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} /></label>
          <label style={{ fontSize: 12 }}>Currency<select style={box} value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })}><option>USD</option><option>LBP</option></select></label>
          <label style={{ fontSize: 12, gridColumn: '1/-1' }}>Paid from<select style={box} value={f.payment_method_id} onChange={e => setF({ ...f, payment_method_id: e.target.value })}>
            <option value="">Not paid yet (owed)</option>{paymentMethods.filter(m => m.type !== 'cheque').map(m => <option key={m._id} value={m._id}>{m.name}</option>)}</select></label>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={busy || !(Number(f.amount) > 0)} style={{ background: '#2e9f86', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>Save</button>
        </div>
      </div>
    </div>
  );
}
